"use client"

import {
  useEffect,
  useId,
  useRef,
  useState,
  type ComponentPropsWithoutRef,
  type MouseEventHandler,
  type ReactNode,
} from "react"
import { VisuallyHidden } from "@radix-ui/react-visually-hidden"
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@ui/shad/alert-dialog"
import { Button } from "@ui/shad/button"
import { Dialog, DialogContent, DialogDescription, DialogTitle, DialogTrigger } from "@ui/shad/dialog"
import { Input } from "@ui/shad/input"
import { Label } from "@ui/shad/label"
import { cn } from "@ui/utils"
import { Loader2, X } from "lucide-react"

import { appDialogClasses, appDialogStyles } from "./app-dialog-config"
import { createDialogCloseRegistry, matchesDestructionConfirmation, requestDialogClose } from "./dialog-behavior"
import { DialogCloseContext, useDialogCloseRegistry } from "./dialog-close-context"
import { DialogEntryContext, useDialogEntryId } from "./dialog-entry-context"

export interface DialogAction {
  label: ReactNode
  onClick?: MouseEventHandler<HTMLButtonElement>
  form?: string
  disabled?: boolean
  pending?: boolean
  destructive?: boolean
  closeAfterClick?: boolean
}
interface Common {
  open?: boolean
  defaultOpen?: boolean
  trigger?: ReactNode
  onOpenChange?: (open: boolean) => void
  title: ReactNode
  description?: ReactNode
  logo?: ReactNode
  children?: ReactNode
  /** Owns discard confirmation when supplied. Must eventually update open. */
  onClose?: () => void
  onOpenAutoFocus?: ComponentPropsWithoutRef<typeof DialogContent>["onOpenAutoFocus"]
  onCloseAutoFocus?: ComponentPropsWithoutRef<typeof DialogContent>["onCloseAutoFocus"]
  /** Keep modal interactions from reaching the table row that opened it. */
  isolateEvents?: boolean
  dismiss?: "all" | "explicit" | "none"
  navigation?: { label: string; onClick: () => void; disabled?: boolean }
}
type Standard = Common & {
  variant: "simpleForm" | "gridForm"
  action?: DialogAction
  cancel?: { label?: string; onClick?: () => void; disabled?: boolean }
}
type Generic = Omit<Standard, "variant"> & { variant: "generic"; secondaryAction?: DialogAction }
type Fullscreen = Omit<Standard, "variant"> & { variant: "fullscreen"; secondaryAction?: DialogAction }
type Information = Common & { variant: "information"; action?: never; cancel?: never }
type Confirmation = Common & {
  variant: "alert"
  action: DialogAction
  cancel?: { label?: string; onClick?: () => void; disabled?: boolean }
}
type Destroy = Omit<Confirmation, "variant"> & { variant: "destroy"; matchText: string }
type Custom = Common & {
  variant: "custom"
  modal?: boolean
  customLayout?: "fullscreen" | "narrow"
  showCloseButton?: boolean
  titleProvided?: boolean
  customClassName?: string
  overlayClassName?: string
  scrollable?: boolean
  contentProps?: Omit<ComponentPropsWithoutRef<typeof DialogContent>, "children" | "className">
}
export type AppDialogProps = Standard | Fullscreen | Generic | Information | Confirmation | Destroy | Custom

/** Controlled dialogs stay in their caller's React tree, preserving context and reactive props. */
export function AppDialogSurface(props: AppDialogProps) {
  const closeRegistry = useDialogCloseRegistry()
  const entryId = useDialogEntryId()
  const titleId = entryId ? `app-dialog-title-${entryId}` : undefined
  const inputId = useId()
  const [confirmation, setConfirmation] = useState("")
  useEffect(() => {
    if (props.open) setConfirmation("")
  }, [props.open])
  const isAlert = props.variant === "alert" || props.variant === "destroy"
  const action = "action" in props ? props.action : undefined
  const cancel = "cancel" in props ? props.cancel : undefined
  const secondary = "secondaryAction" in props ? props.secondaryAction : undefined
  const close = () => {
    requestDialogClose(props.dismiss ?? "all", () => props.onOpenChange?.(false), props.onClose ?? closeRegistry?.get())
  }
  const Description = isAlert ? AlertDialogDescription : DialogDescription
  const Cancel = isAlert ? AlertDialogCancel : null
  const cancelButton = (
    <Button
      type="button"
      variant="outline"
      disabled={cancel?.disabled || props.dismiss === "none"}
      onClick={(event) => {
        event.preventDefault()
        if (props.onClose || closeRegistry?.get()) close()
        else (cancel?.onClick ?? close)()
      }}
    >
      {cancel?.label ?? "Cancel"}
    </Button>
  )
  const title = isAlert ? (
    <AlertDialogTitle>{props.title}</AlertDialogTitle>
  ) : (
    <DialogTitle {...(titleId ? { id: titleId } : {})}>{props.title}</DialogTitle>
  )
  return props.variant === "custom" ? (
    <>
      {!props.titleProvided && <VisuallyHidden>{title}</VisuallyHidden>}
      {props.children}
      {props.showCloseButton && (
        <Button
          type="button"
          variant="ghost"
          size="icon"
          className="absolute top-4 right-4"
          onClick={close}
          disabled={props.dismiss === "none"}
          aria-label="Close"
        >
          <X />
        </Button>
      )}
    </>
  ) : (
    <>
      <div
        className={
          isAlert
            ? appDialogStyles.confirmationHeader
            : cn(appDialogStyles.header, props.logo && appDialogStyles.logoHeader)
        }
      >
        {props.navigation && (
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={props.navigation.disabled}
            onClick={props.navigation.onClick}
          >
            {props.navigation.label}
          </Button>
        )}
        {props.logo}
        <div className="min-w-0 flex-1">{title}</div>
        {!isAlert && (
          <Button
            type="button"
            variant="ghost"
            size="iconXs"
            aria-label="Close"
            onClick={close}
            disabled={props.dismiss === "none"}
          >
            <X />
          </Button>
        )}
      </div>
      <div className={isAlert ? appDialogStyles.confirmationBody : appDialogStyles.body}>
        {props.description && (
          <Description asChild>
            <div className={cn("text-sm font-normal", isAlert ? "text-tertiary-foreground" : "text-foreground")}>
              {props.description}
            </div>
          </Description>
        )}
        {props.children}
        {props.variant === "destroy" && (
          <div className="space-y-1">
            <Label htmlFor={inputId}>Type &quot;{props.matchText}&quot; to confirm</Label>
            <Input
              id={inputId}
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              placeholder={props.matchText}
              autoComplete="off"
            />
          </div>
        )}
      </div>
      {action && (
        <div className={isAlert ? appDialogStyles.confirmationFooter : appDialogStyles.footer}>
          {secondary ? (
            <Button
              type="button"
              variant="outline"
              disabled={secondary.disabled || secondary.pending}
              onClick={(event) => {
                secondary.onClick?.(event)
                if (secondary.closeAfterClick && !event.defaultPrevented) close()
              }}
            >
              {secondary.pending && <Loader2 className="animate-spin" />}
              {secondary.label}
            </Button>
          ) : Cancel ? (
            <Cancel asChild>{cancelButton}</Cancel>
          ) : (
            cancelButton
          )}
          <Button
            type={action.form ? "submit" : "button"}
            form={action.form}
            variant={action.destructive || props.variant === "destroy" ? "destructive" : "primary"}
            disabled={
              action.disabled ||
              action.pending ||
              (props.variant === "destroy" && !matchesDestructionConfirmation(confirmation, props.matchText))
            }
            onClick={(event) => {
              action.onClick?.(event)
              if (action.closeAfterClick && !event.defaultPrevented) close()
            }}
          >
            {action.pending && <Loader2 className="animate-spin" />}
            {action.label}
          </Button>
        </div>
      )}
    </>
  )
}

/** Internal composition shared by named public components and existing migration adapters. */
export function AppDialog(props: AppDialogProps) {
  const [closeRegistry] = useState(createDialogCloseRegistry)
  const [localOpen, setLocalOpen] = useState(props.defaultOpen ?? false)
  const returnFocusRef = useRef<HTMLElement | null>(null)
  const open = props.open ?? localOpen
  const handleOpenAutoFocus: NonNullable<Common["onOpenAutoFocus"]> = (event) => {
    returnFocusRef.current = document.activeElement instanceof HTMLElement ? document.activeElement : null
    const onOpenAutoFocus =
      props.onOpenAutoFocus ?? (props.variant === "custom" ? props.contentProps?.onOpenAutoFocus : undefined)
    onOpenAutoFocus?.(event)
  }
  const handleCloseAutoFocus: NonNullable<Common["onCloseAutoFocus"]> = (event) => {
    const onCloseAutoFocus =
      props.onCloseAutoFocus ?? (props.variant === "custom" ? props.contentProps?.onCloseAutoFocus : undefined)
    onCloseAutoFocus?.(event)
    if (event.defaultPrevented || props.trigger || !returnFocusRef.current?.isConnected) return
    // Controlled callers have no Radix Trigger; return focus to their actual opener.
    event.preventDefault()
    returnFocusRef.current.focus({ preventScroll: true })
  }
  const notifyOpenChange = (nextOpen: boolean) => {
    setLocalOpen(nextOpen)
    props.onOpenChange?.(nextOpen)
  }
  const isAlert = props.variant === "alert" || props.variant === "destroy"
  const close = () => {
    requestDialogClose(props.dismiss ?? "all", () => notifyOpenChange(false), props.onClose ?? closeRegistry.get())
  }
  const explicit = isAlert || props.dismiss === "explicit" || props.dismiss === "none"
  const body = (
    <DialogCloseContext.Provider value={closeRegistry}>
      <DialogEntryContext.Provider value={null}>
        <AppDialogSurface {...props} open={open} onOpenChange={notifyOpenChange} />
      </DialogEntryContext.Provider>
    </DialogCloseContext.Provider>
  )
  const classes =
    props.variant === "custom"
      ? cn(
          props.customLayout === "fullscreen" && appDialogClasses("fullscreen"),
          props.customLayout === "narrow" && appDialogClasses("narrow"),
          props.customClassName
        )
      : appDialogClasses(props.variant)
  if (isAlert)
    return (
      <AlertDialog open={open} onOpenChange={(nextOpen) => (nextOpen ? notifyOpenChange(true) : close())}>
        {props.trigger && <AlertDialogTrigger asChild>{props.trigger}</AlertDialogTrigger>}
        <AlertDialogContent
          className={classes}
          onOpenAutoFocus={handleOpenAutoFocus}
          onCloseAutoFocus={handleCloseAutoFocus}
          {...(!props.description ? { "aria-describedby": undefined } : {})}
          onEscapeKeyDown={(event) => {
            event.preventDefault()
            if (props.dismiss !== "none") close()
          }}
        >
          {body}
        </AlertDialogContent>
      </AlertDialog>
    )
  return (
    <Dialog
      open={open}
      modal={props.variant === "custom" ? props.modal : undefined}
      onOpenChange={(nextOpen) => (nextOpen ? notifyOpenChange(true) : close())}
    >
      {props.trigger && <DialogTrigger asChild>{props.trigger}</DialogTrigger>}
      <DialogContent
        {...(props.variant === "custom" ? props.contentProps : {})}
        className={classes}
        overlayClassName={props.variant === "custom" ? props.overlayClassName : undefined}
        {...(!props.description ? { "aria-describedby": undefined } : {})}
        scrollable={props.variant === "custom" && props.scrollable}
        onOpenAutoFocus={handleOpenAutoFocus}
        onCloseAutoFocus={handleCloseAutoFocus}
        onSubmit={(event) => {
          if (props.variant === "custom") props.contentProps?.onSubmit?.(event)
          // Portal events still bubble through React parents, including other forms.
          event.stopPropagation()
        }}
        onPointerDown={(event) => {
          if (props.variant === "custom") props.contentProps?.onPointerDown?.(event)
          if (props.isolateEvents) event.stopPropagation()
        }}
        onClick={(event) => {
          if (props.variant === "custom") props.contentProps?.onClick?.(event)
          if (props.isolateEvents) event.stopPropagation()
        }}
        onEscapeKeyDown={(event) => {
          if (props.variant === "custom") props.contentProps?.onEscapeKeyDown?.(event)
          if (props.dismiss === "none") event.preventDefault()
        }}
        onInteractOutside={(event) => {
          const target = event.target
          if (
            target instanceof Element &&
            target.closest(".tsqd-parent-container, [data-voice-transcription-pill], .picker-dialog, .picker-dialog-bg")
          ) {
            event.preventDefault()
            return
          }
          if (props.variant === "custom") props.contentProps?.onInteractOutside?.(event)
          if (explicit) event.preventDefault()
        }}
      >
        {body}
      </DialogContent>
    </Dialog>
  )
}
